/** Small presentational primitives shared across portal pages. */
import React, { useState } from 'react';
import { CvsiError, ReasonCodeCatalog } from '@cvsi/shared';
import { eventTypePresentation, riskLabel } from '../lib/terminology';

const SEVERITY_COLOR: Record<string, string> = { info: 'var(--accent)', low: 'var(--good)', medium: 'var(--monitor)', high: 'var(--bad)' };

export function PageHeader({ title, subtitle, actions }: { title: string; subtitle?: string; actions?: React.ReactNode }) {
  return (
    <div className="topbar">
      <div>
        <h1>{title}</h1>
        {subtitle && <div className="muted">{subtitle}</div>}
      </div>
      {actions && <div className="row">{actions}</div>}
    </div>
  );
}

export function Loading({ rows = 3 }: { rows?: number }) {
  return (
    <div className="card">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton" style={{ marginBottom: 10, width: `${90 - i * 12}%` }} />
      ))}
    </div>
  );
}

export function ErrorView({ error, onRetry }: { error: Error | CvsiError; onRetry?: () => void }) {
  const isAuth = error instanceof CvsiError && (error.kind === 'unauthenticated' || error.kind === 'forbidden');
  return (
    <div className="error">
      <strong>{error instanceof CvsiError ? error.kind : 'error'}</strong>: {error.message}
      {isAuth && <div className="muted" style={{ marginTop: 6 }}>Your session may lack the required scope, or has expired.</div>}
      {onRetry && <div style={{ marginTop: 10 }}><button className="secondary small" onClick={onRetry}>Retry</button></div>}
    </div>
  );
}

export function CustomerSafeState({
  kind,
  title,
  message,
  impact,
  referenceId,
  action,
  onRetry,
}: {
  kind: 'empty' | 'stale' | 'degraded' | 'error' | 'blocking';
  title: string;
  message: string;
  impact?: string;
  referenceId?: string;
  action?: React.ReactNode;
  onRetry?: () => void;
}) {
  const tone = kind === 'error' || kind === 'blocking' ? 'error' : 'notice';
  return (
    <div className={tone} role={kind === 'error' || kind === 'blocking' ? 'alert' : 'status'}>
      <strong>{title}</strong>
      <div style={{ marginTop: 4 }}>{message}</div>
      {impact && <div className="muted" style={{ marginTop: 6 }}>Impact: {impact}</div>}
      {referenceId && <div className="muted" style={{ marginTop: 6 }}>Reference: <span className="mono">{referenceId}</span></div>}
      {(onRetry || action) && (
        <div className="row wrap" style={{ marginTop: 10 }}>
          {onRetry && <button className="secondary small" onClick={onRetry}>Retry</button>}
          {action}
        </div>
      )}
    </div>
  );
}

export function StaleDataNotice({
  source,
  ageLabel,
  lastSuccess,
  impact,
  onRefresh,
}: {
  source: string;
  ageLabel?: string;
  lastSuccess?: string;
  impact: string;
  onRefresh?: () => void;
}) {
  return (
    <CustomerSafeState
      kind="stale"
      title={`${source} data may be stale`}
      message={`${ageLabel ? `Newest data is ${ageLabel} old. ` : ''}${lastSuccess ? `Last successful update: ${lastSuccess}.` : 'A last successful update timestamp is not available.'}`}
      impact={impact}
      onRetry={onRefresh}
    />
  );
}

export function StatusPill({ status }: { status: string }) {
  const cls = ['active', 'verified', 'reviewed', 'good', 'resolved', 'delivered', 'low', 'accepted'].includes(status)
    ? 'good'
    : ['suspended', 'failed', 'bad', 'revoked', 'closed', 'review_required', 'manual_review', 'rejected'].includes(status)
      ? 'bad'
      : ['elevated'].includes(status)
        ? 'warn'
        : ['monitor'].includes(status)
          ? 'monitor'
          : ['sandbox', 'pending', 'open', 'warn'].includes(status)
            ? 'warn'
            : '';
  return <span className={`pill ${cls}`}>{riskLabel(status)}</span>;
}

export function EventTypeBadge({ eventType, compact = false }: { eventType: string | null | undefined; compact?: boolean }) {
  const presentation = eventTypePresentation(eventType);
  return <span className={`event-type-badge event-type-badge--${presentation.tone}`} title={presentation.description}>
    <span className="event-type-badge-dot" aria-hidden="true" />
    <span>{compact ? presentation.shortLabel : presentation.label}</span>
  </span>;
}

export function ReadableList({ items, empty = 'None recorded' }: { items: string[]; empty?: string }) {
  if (!items.length) return <span className="muted">{empty}</span>;
  return <ul className="readable-list">{items.map((item, index) => <li key={`${item}-${index}`}>{item}</li>)}</ul>;
}

export function CopyableSecret({ value, label }: { value: string; label?: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div>
      {label && <label>{label}</label>}
      <div className="secret">{value}</div>
      <div style={{ marginTop: 8 }}>
        <button
          className="secondary small"
          onClick={async () => {
            try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
          }}
        >
          {copied ? 'Copied ✓' : 'Copy'}
        </button>
      </div>
    </div>
  );
}

export function Empty({ children }: { children: React.ReactNode }) {
  return <div className="muted" style={{ padding: '14px 2px' }}>{children}</div>;
}

export function Kpi({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <div className="muted" style={{ fontSize: 12 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 600 }}>{value}</div>
    </div>
  );
}

/** Use for one record's attributes; tables are reserved for comparing multiple records. */
export function DataFields({ fields, title }: {
  title?: string;
  fields: Array<{ key?: string; label: string; value: React.ReactNode; mono?: boolean }>;
}) {
  return <section className="data-fields-section">
    {title && <h4>{title}</h4>}
    <dl className="data-fields">
      {fields.map(({ key, label, value, mono }) => <div key={key ?? label} className="data-field">
        <dt>{label}</dt>
        <dd className={mono ? 'mono' : undefined}>{value === null || value === undefined || value === '' ? <span className="missing-value">Not available</span> : value}</dd>
      </div>)}
    </dl>
  </section>;
}

/** Tamper-evident hash display: truncated with the full value in a tooltip (mirrors the
 *  audit-chain convention in apps/internal-admin), a copy button, and an integrity pill. */
export function IntegrityFingerprint({ hash, tamperEvident, onVerify }: {
  hash: string;
  tamperEvident: boolean | null;
  onVerify?: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="row wrap" style={{ gap: 10, alignItems: 'center' }}>
      <span className="fingerprint mono" title={hash}>{hash.slice(0, 16)}…</span>
      <button
        className="secondary small"
        onClick={async () => {
          try { await navigator.clipboard.writeText(hash); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch { /* clipboard blocked */ }
        }}
      >
        {copied ? 'Copied ✓' : 'Copy full hash'}
      </button>
      {tamperEvident != null && (
        <span className={`pill ${tamperEvident ? 'good' : 'bad'}`}>
          {tamperEvident ? 'Verified intact' : 'Integrity check failed'}
        </span>
      )}
      {onVerify && <button className="secondary small" onClick={onVerify}>Re-verify</button>}
    </div>
  );
}

/** Chain-of-custody / provenance summary — who/what generated the record, retention, model version. */
export function ProvenancePanel({ generatedAt, retentionUntil, modelVersion, tenantName }: {
  generatedAt: string;
  retentionUntil?: string | null;
  modelVersion?: string | null;
  tenantName?: string;
}) {
  return <DataFields title="Provenance" fields={[
    { label: 'Generated for', value: tenantName },
    { label: 'Generated at', value: new Date(generatedAt).toLocaleString() },
    { label: 'Retained until', value: retentionUntil ? new Date(retentionUntil).toLocaleDateString() : null },
    { label: 'Model version', value: modelVersion, mono: true },
  ]} />;
}

/**
 * Honest placeholder for a widget with no real data source behind it yet (e.g. a live map with no
 * real-time feed wired up). Never fake the visual with sample data — say plainly what's missing
 * and, where known, what would connect it.
 */
export function PlaceholderPanel({ title, message }: { title: string; message: string }) {
  return (
    <div className="placeholder-panel">
      <div className="ph-title">{title}</div>
      <div className="ph-body">{message}</div>
    </div>
  );
}

/**
 * Onboarding empty state (UX Implementation Guide §2.1/§2.2/§5.5): explains the value, prerequisites,
 * next step, owner, and expected time instead of a bare API instruction, so an unconfigured feature
 * reads as "not set up yet" rather than "broken."
 */
export function OnboardingEmpty({
  title,
  value,
  steps,
  owner,
  eta,
}: {
  title: string;
  value: string;
  steps: string[];
  owner?: string;
  eta?: string;
}) {
  return (
    <div className="onboarding-empty">
      <h4>{title}</h4>
      <div className="muted">{value}</div>
      <ol>
        {steps.map((s, i) => <li key={i}>{s}</li>)}
      </ol>
      {(owner || eta) && (
        <div className="meta">
          {owner && <span>Owner: <strong style={{ color: 'var(--text)' }}>{owner}</strong></span>}
          {eta && <span>Expected time: <strong style={{ color: 'var(--text)' }}>{eta}</strong></span>}
        </div>
      )}
    </div>
  );
}

/** A reason code rendered as its published catalog description instead of a raw code string,
 *  colored by severity, falling back to the raw code if the catalog hasn't loaded or doesn't know it. */
export function ReasonCodeChip({ code, catalog }: { code: string; catalog: ReasonCodeCatalog | null | undefined }) {
  const meta = catalog?.codes.find((c) => c.code === code);
  return (
    <span
      className="pill"
      title={meta ? `${meta.category} · ${meta.remediation}` : code}
      style={meta ? { borderColor: SEVERITY_COLOR[meta.severity], color: SEVERITY_COLOR[meta.severity] } : undefined}
    >
      {meta ? meta.example : code.replace(/_/g, ' ')}
    </span>
  );
}
